import {useEffect,useState,type FormEvent} from 'react';
import {SupportNotifications} from './SupportNotifications';
type View='none'|'summary'|'composer'|'notifications'|'conversations';
type Member={staffId:string;displayName:string};
type Thread={recipientId:string;displayName:string;lastMessage:string;
  lastMessageAt:string;lastMessageFromSelf:boolean};
type Message={id:string;senderActorId:string;recipientActorId:string;
  body:string;createdAt:string};
async function getData<T>(url:string,signal?:AbortSignal):Promise<T>{
  const response=await fetch(url,{credentials:'same-origin',cache:'no-store',signal});
  const json=await response.json() as {ok:boolean;data?:T};
  if(!response.ok||!json.ok||!json.data)throw Error('MESSAGES_UNAVAILABLE');
  return json.data;
}
/** Original Helm header message icon, now linked to actual staff-private records.
 * Notifications stay clearly unconnected until a real notification feed exists.
 */
export function StaffCommunications({locationCode,onOpenSupportTask}:{
  locationCode:string;onOpenSupportTask:(taskId:string)=>void;
}){
  const [view,setView]=useState<View>('none');
  const [threads,setThreads]=useState<Thread[]>([]);
  const [messages,setMessages]=useState<Message[]>([]);
  const [recipient,setRecipient]=useState<Member|null>(null);
  const [query,setQuery]=useState('');
  const [matches,setMatches]=useState<Member[]>([]);
  const [draft,setDraft]=useState('');
  const [status,setStatus]=useState('');
  const [sending,setSending]=useState(false);
  const [key,setKey]=useState<string|null>(null);
  const close=()=>{setView('none');setStatus('');};
  const toggle=()=>setView(previous=>previous==='summary'?'none':'summary');
  const fullPage=view==='notifications'||view==='conversations';
  const refreshThreads=async(signal?:AbortSignal)=>{
    const data=await getData<{threads:Thread[]}>(
      '/api/v1/staff/private-messages?'+new URLSearchParams({location:locationCode}),
      signal);
    if(!signal?.aborted)setThreads(data.threads);
  };
  useEffect(()=>{
    setView('none');setRecipient(null);setThreads([]);setMessages([]);
    setDraft('');setQuery('');setStatus('');setKey(null);
  },[locationCode]);
  useEffect(()=>{
    if(view==='none'||view==='notifications')return;
    const abort=new AbortController();
    void refreshThreads(abort.signal).catch(()=>{
      if(!abort.signal.aborted)setStatus('Unable to load private conversations.');
    });
    return ()=>abort.abort();
  },[view,locationCode]);
  useEffect(()=>{
    if(!recipient||view==='none')return;
    const abort=new AbortController();
    const load=()=>getData<{messages:Message[]}>(
      '/api/v1/staff/private-messages/'+encodeURIComponent(recipient.staffId)+'?'+
        new URLSearchParams({location:locationCode}),abort.signal)
      .then(data=>{if(!abort.signal.aborted)setMessages(data.messages);})
      .catch(()=>{if(!abort.signal.aborted)setStatus('Could not load conversation.');});
    void load();
    const poll=window.setInterval(()=>void load(),15000);
    return ()=>{window.clearInterval(poll);abort.abort();};
  },[recipient?.staffId,view,locationCode]);
  useEffect(()=>{
    const term=query.trim();
    if(term.length<2){setMatches([]);return;}
    const abort=new AbortController();
    const timer=window.setTimeout(()=>{
      void getData<{teamMembers:Member[]}>(
        '/api/v1/staff/team/search?'+new URLSearchParams({
          location:locationCode,q:term,
        }),abort.signal)
        .then(data=>{if(!abort.signal.aborted)setMatches(data.teamMembers);})
        .catch(()=>{if(!abort.signal.aborted)setMatches([]);});
    },200);
    return ()=>{window.clearTimeout(timer);abort.abort();};
  },[query,locationCode]);
  const choose=(member:Member)=>{
    setRecipient(member);setMessages([]);setQuery('');setMatches([]);
    setStatus('');setView('composer');setKey(null);
  };
  const send=async(event:FormEvent)=>{
    event.preventDefault();
    if(!recipient||!draft.trim()||sending)return;
    setSending(true);setStatus('');
    const requestKey=key??crypto.randomUUID();setKey(requestKey);
    try{
      const response=await fetch('/api/v1/staff/private-messages',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,recipientId:recipient.staffId,
          body:draft.trim(),idempotencyKey:requestKey}),
      });
      const json=await response.json() as {ok:boolean;data?:Message;error?:{code:string}};
      if(!response.ok||!json.ok||!json.data)
        throw Error(json.error?.code??'Message was not sent.');
      setMessages(current=>current.some(m=>m.id===json.data!.id)?current:
        [...current,json.data!]);
      setDraft('');setKey(null);setStatus('Message sent.');
      void refreshThreads().catch(()=>{});
    }catch(error){setStatus(error instanceof Error?error.message:'Message was not sent.');}
    finally{setSending(false);}
  };
  const threadList=<div className="ecfs-private-threads" aria-label="Private conversations">
    {threads.length===0&&<p>No private conversations yet.</p>}
    {threads.map(item=><button type="button" key={item.recipientId}
      onClick={()=>choose({staffId:item.recipientId,displayName:item.displayName})}>
      <strong>{item.displayName}</strong>
      <small>{item.lastMessageFromSelf?'You: ':''}{item.lastMessage}</small>
    </button>)}
  </div>;
  const editor=<div className="ecfs-composer">
    {recipient?<><strong>{recipient.displayName}</strong>
      <div className="ecfs-private-message-history" aria-label="Message history">
        {messages.map(m=><p key={m.id}><small>{m.senderActorId===recipient.staffId
          ?recipient.displayName:'You'}</small> {m.body}</p>)}
      </div>
      <form onSubmit={event=>void send(event)}>
        <label>Message
          <textarea aria-label="Private message" value={draft} rows={4}
            maxLength={4000} onChange={event=>{setDraft(event.target.value);setKey(null);}}/>
        </label>
        <button type="submit" disabled={sending||!draft.trim()}>
          {sending?'Sending…':'Send message'}</button>
      </form>
    </>:<><label>To
      <input aria-label="Message recipient" placeholder="Choose a Team Member"
        value={query} onChange={e=>setQuery(e.target.value)}/>
    </label>
      {matches.map(item=><button type="button" key={item.staffId}
        onClick={()=>choose(item)}>{item.displayName}</button>)}
    </>}
  </div>;
  return <div className="ecfs-messaging-root">
    <button type="button" className="ecfs-message-trigger"
      aria-label="Messages and notifications" aria-expanded={view!=='none'}
      onClick={toggle}>
      <svg aria-hidden="true" width="20" height="19" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M4 4h16v12H8l-4 4V4Z"/>
      </svg>
    </button>
    {view==='summary'&&<aside role="dialog" className="ecfs-messaging-popover"
      aria-label="Notifications and private messages">
      <div className="ecfs-messaging-title">
        <strong>Notifications</strong>
        <div><button type="button" className="ecfs-messaging-link"
          onClick={()=>setView('conversations')}>Private Conversations</button>
          <button type="button" aria-label="Close notifications" onClick={close}>×</button>
        </div>
      </div>
      <SupportNotifications locationCode={locationCode} onOpenTask={onOpenSupportTask}/>
      {threadList}
      <button type="button" className="ecfs-messaging-footer"
        onClick={()=>setView('notifications')}>View all notifications</button>
    </aside>}
    {view==='composer'&&<aside role="dialog" className="ecfs-messaging-popover"
      aria-label="Private conversation composer">
      <div className="ecfs-messaging-title">
        <strong>Private Conversations</strong>
        <button type="button" aria-label="Close private conversation composer"
          onClick={close}>×</button>
      </div>
      {editor}
      {status&&<p role="status">{status}</p>}
      <button type="button" className="ecfs-messaging-footer"
        onClick={()=>setView('conversations')}>View all private conversations</button>
    </aside>}
    {fullPage&&<section role="dialog" aria-modal="true"
      className="ecfs-messaging-page"
      aria-label={view==='notifications'?'All notifications':'All private conversations'}>
      <div className="ecfs-messaging-page-heading">
        <button type="button" onClick={()=>setView('summary')}>← Back</button>
        <h2>{view==='notifications'?'Notifications':'Private Conversations'}</h2>
        <button type="button" aria-label="Close messages" onClick={close}>×</button>
      </div>
      <div className="ecfs-messaging-page-body">
        {view==='notifications'
          ?<SupportNotifications locationCode={locationCode} onOpenTask={onOpenSupportTask}/>
          :<><button type="button" onClick={()=>{setRecipient(null);setView('composer');}}>
              Compose private message</button>
            {threadList}{recipient&&editor}{status&&<p role="status">{status}</p>}</>}
      </div>
    </section>}
  </div>;
}
